import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { ArrowUpRight, Clock, ArrowRight } from 'lucide-react';
import { blogPosts } from '../data/blog';

export default function Insights() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.15 });
  const posts = blogPosts.slice(0, 3);

  if (posts.length === 0) return null;

  return (
    <section id="insights" className="py-28 md:py-40 bg-[#050A08] relative overflow-hidden" data-testid="insights-section">
      <div className="absolute top-1/3 left-0 w-[500px] h-[500px] bg-emerald-500/[0.06] rounded-full blur-[150px] pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-16"
        >
          <div className="max-w-2xl">
            <span className="inline-block text-emerald-400 font-semibold text-sm uppercase tracking-[0.25em] mb-5">
              / Journal
            </span>
            <h2 className="font-display text-4xl md:text-6xl font-black text-white leading-[0.95]">
              Ideas for building<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-lime-400">
                a leaner business.
              </span>
            </h2>
          </div>
          <Link
            to="/blog"
            className="group inline-flex items-center gap-2 text-emerald-400 hover:text-emerald-300 font-semibold transition-colors duration-300 self-start md:self-auto"
            data-testid="insights-view-all"
          >
            View all posts
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
          </Link>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {posts.map((post, i) => (
            <motion.div
              key={post.slug}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.15 + i * 0.1 }}
            >
              <Link
                to={`/blog/${post.slug}`}
                className="group block h-full rounded-2xl overflow-hidden bg-[#0D1612] border border-white/[0.06] hover:border-emerald-500/40 transition-colors duration-500"
                data-testid={`insights-card-${post.slug}`}
              >
                <div className="h-48 overflow-hidden">
                  <img
                    src={post.coverImage}
                    alt={post.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <div className="p-6 md:p-7">
                  <div className="flex items-center gap-3 text-xs text-slate-500 mb-4">
                    <span className="text-emerald-400 uppercase tracking-[0.2em] font-semibold">{post.category}</span>
                    <span className="w-1 h-1 rounded-full bg-slate-600" />
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{post.readTime}</span>
                  </div>
                  <h3 className="font-display text-xl font-bold text-white leading-snug mb-3 group-hover:text-emerald-300 transition-colors duration-300">
                    {post.title}
                  </h3>
                  <p className="text-slate-400 text-sm leading-relaxed mb-5 line-clamp-3">{post.excerpt}</p>
                  <div className="flex items-center justify-between pt-4 border-t border-white/[0.06]">
                    <span className="text-slate-500 text-xs">{post.date}</span>
                    <span className="w-9 h-9 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center group-hover:bg-emerald-500 transition-colors duration-300">
                      <ArrowUpRight className="w-4 h-4 text-emerald-400 group-hover:text-[#04120C] transition-colors duration-300" />
                    </span>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
